import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ErrorBannerService } from '../services/error-banner.service';
import { AppErrorService } from '../services/app-error.service';
import { SystemToastService } from '../services/system-toast.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private static readonly AUTH_LOGOUT_DELAY_MS = 1100;

  constructor(
    private authService: AuthService,
    private errorBannerService: ErrorBannerService,
    private appErrorService: AppErrorService,
    private systemToastService: SystemToastService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isPostLoginContext = this.authService.isAuthenticated();
        const resolved = this.appErrorService.resolve(error);
        const shouldForceLogout = this.appErrorService.shouldForceLogoutByCode(
          resolved.code,
          resolved.rawMessage
        );
        (error as any).appError = resolved;
        (error as any).appErrorBannerShown = false;
        (error as any).appErrorPostLogin = isPostLoginContext;

        if (isPostLoginContext) {
          if (shouldForceLogout) {
            this.systemToastService.show({
              title: 'Error',
              message: resolved.message,
            });

            setTimeout(() => {
              this.authService.forceLogout();
            }, HttpErrorInterceptor.AUTH_LOGOUT_DELAY_MS);
          } else if (
            this.shouldShowBannerForRequest(req, resolved.code, isPostLoginContext)
          ) {
            this.errorBannerService.show(resolved.message);
            (error as any).appErrorBannerShown = true;
          }
        }

        console.error('HTTP Error:', error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  private shouldShowBannerForRequest(
    req: HttpRequest<any>,
    code: string | null,
    isPostLoginContext: boolean
  ): boolean {
    if (!isPostLoginContext) {
      return false;
    }

    if (this.isCreateCertificationRequest(req)) {
      return false;
    }

    if (this.isSubmitMethod(req.method)) {
      return true;
    }

    return this.appErrorService.shouldShowBannerByCode(code, req.url);
  }

  private isCreateCertificationRequest(req: HttpRequest<any>): boolean {
    const normalizedMethod = String(req.method ?? '').toUpperCase();
    if (normalizedMethod !== 'POST') {
      return false;
    }

    const requestUrl = String(req.url ?? '').split('?')[0].replace(/\/+$/, '');
    return requestUrl.toLowerCase().endsWith('/certificationrequests');
  }

  private isSubmitMethod(method: string): boolean {
    const normalized = String(method ?? '').toUpperCase();
    return (
      normalized === 'POST' ||
      normalized === 'PUT' ||
      normalized === 'PATCH' ||
      normalized === 'DELETE'
    );
  }
}
